import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Leaf, Sparkles, Loader2, Lock, Wand2 } from "lucide-react";
import { toast } from "sonner";

const CAT_ACCENT = {
  Digestion: "bg-secondary/15 text-secondary",
  "Detox & Liver": "bg-primary/10 text-primary",
  "Blood Sugar": "bg-accent/40 text-accent-foreground",
  Immunity: "bg-secondary/15 text-secondary",
  "Energy & Stress": "bg-primary/10 text-primary",
};

export default function Herbs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSug, setLoadingSug] = useState(false);

  useEffect(() => {
    api.get("/herbs").then(({ data }) => setCategories(data.categories));
  }, []);

  const getSuggestions = async () => {
    setLoadingSug(true);
    try {
      const { data } = await api.get("/herbs/suggestions");
      setSuggestions(data);
    } catch (err) {
      if (err.response?.status === 402) navigate("/pricing");
      else toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingSug(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="fade-up">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Natural support</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black mt-1">Beneficial herbs</h1>
          <p className="text-muted-foreground mt-1">Herbs to support digestion, detox & body functions.</p>
        </div>

        {/* AI suggestions */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 fade-up" data-testid="herb-suggestions-card">
          <div className="flex items-center gap-2 mb-1">
            <Wand2 className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-xl font-bold">Personalized for you</h2>
            {!user?.is_premium && <span className="text-xs font-bold uppercase tracking-wider text-accent-foreground bg-accent/40 px-2 py-0.5 rounded-full ml-1">Premium</span>}
          </div>
          <p className="text-muted-foreground text-sm mb-5">AI picks from these herbs based on your recent sugar & gut logs.</p>

          {!user?.is_premium ? (
            <div className="text-center py-4">
              <Lock className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground mb-4">Unlock AI herb suggestions tailored to your data.</p>
              <Button onClick={() => navigate("/pricing")} className="rounded-full font-bold" data-testid="herbs-upgrade-btn">
                <Sparkles className="w-4 h-4 mr-1" /> Go Premium
              </Button>
            </div>
          ) : suggestions ? (
            <div className="space-y-3" data-testid="herb-suggestions-list">
              <p className="text-xs text-muted-foreground">Based on avg {suggestions.avg_sugar}g sugar/day · {suggestions.bowel_count} gut logs.</p>
              {suggestions.suggestions.map((s, i) => (
                <div key={i} className="flex items-start gap-3 bg-muted rounded-xl p-4">
                  <Leaf className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">{s.herb}</p>
                    <p className="text-sm text-muted-foreground">{s.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Button onClick={getSuggestions} disabled={loadingSug} className="rounded-full font-bold" data-testid="get-suggestions-btn">
              {loadingSug ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" /> Get my suggestions</>}
            </Button>
          )}
        </div>

        {/* Curated list */}
        {categories.map((cat) => (
          <div key={cat.category} className="fade-up" data-testid={`herb-category-${cat.category}`}>
            <div className="flex items-center gap-2 mb-4">
              <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${CAT_ACCENT[cat.category] || "bg-muted text-muted-foreground"}`}>{cat.category}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {cat.items.map((h) => (
                <div key={h.name} className="bg-card border border-border rounded-2xl p-5 hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform]">
                  <div className="flex items-center gap-2 mb-1">
                    <Leaf className="w-4 h-4 text-secondary" />
                    <p className="font-heading font-bold">{h.name}</p>
                  </div>
                  <p className="text-sm text-foreground/80">{h.benefit}</p>
                  <p className="text-xs text-muted-foreground mt-2"><span className="font-bold">How:</span> {h.how}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        <p className="text-xs text-muted-foreground text-center fade-up">
          Educational information only — not medical advice. Consult a professional before starting any herb, especially if pregnant or on medication.
        </p>
      </div>
    </Layout>
  );
}
